/**
 * Audio Analysis Prompt for Find Audio Context
 * Simplified prompt for fine-tuned model
 */

export const createFindAudioAnalysisPrompt = (collectionData?: { 
  genres: { displayName: string }[], 
  tags: { displayName: string }[],
  categorizedTags?: {
    moods: string[],
    temporal: string[],
    instruments: string[],
    energy: string[],
    other: string[]
  }
}): string => {
  const collectionDataSection = collectionData ? `

### Available Collection Data:

**Genres:**
${collectionData.genres.map(g => `- ${g.displayName}`).join('\n')}

**Categorized Tags:**
${collectionData.categorizedTags ? `
**Moods:** ${collectionData.categorizedTags.moods.join(', ')}
**Temporal:** ${collectionData.categorizedTags.temporal.join(', ')}
**Instruments:** ${collectionData.categorizedTags.instruments.join(', ')}
**Energy:** ${collectionData.categorizedTags.energy.join(', ')}
**Other:** ${collectionData.categorizedTags.other.join(', ')}` : 
`**All Tags:** ${collectionData.tags.map(t => t.displayName).join(', ')}`}

### Category-Based Analysis Process:
1. **Audio Analysis** → identify all musical elements
2. **Category Scoring** → score each category systematically:
   - **Moods**: Score each mood tag (0-100) - select multiple if highly relevant
   - **Temporal**: Score temporal tags (0-100) - ONLY select if score >80. For "contemporary", ONLY if track sounds very modern/current (not just recent)
   - **Instruments**: Score instruments (0-100) - SELECT ONLY ONE hero instrument with highest confidence (>75). Skip if no instrument scores high enough
   - **Energy**: Score energy tags (0-100) - select multiple if relevant
   - **Other**: Score other tags (0-100) - select if highly relevant
3. **Validation Pass** → re-score with fresh ears and validate
4. **Final Selection** → use ONLY high-scoring validated items

**CRITICAL RULES:**
- Temporal tags only if scoring >80 - "contemporary" ONLY for very modern-sounding tracks (not just from recent years)
- Maximum ONE hero instrument - the most prominent and confident only (>75 score)
- Multiple selections allowed per category if scores justify
- One genre maximum from available list` : '';

  return `FIRST: Check track duration and use correct terminology:
- Under 7 seconds: Use "mnemonic" instead of "track"
- 7 to 15 seconds: MUST use "sting" instead of "track"
- Over 15 seconds: Use "track"
${collectionDataSection}

Analyze this audio and provide a description following this pattern:
"[mood] [time period] [genre] [mnemonic/sting/track] with [hero instruments], [additional characteristics] atmosphere"

CRITICAL EXCLUSION RULES:
- Maximum ONE hero instrument only - the most prominent and confident (not background elements)
- DO NOT mention vocals, singing, or any vocal-related terms (male vocals, female vocals, choir, etc.)
- Only use "synth" if the track is explicitly electronic genre
- "Contemporary" ONLY for very modern-sounding tracks, not just recent tracks
- NEVER use tempo tags: "slow tempo", "mid tempo", or "fast tempo" in descriptions

Focus on:
- One primary genre from the available collection
- PRIORITIZE mood and characteristic tags
- Multiple mood descriptors (emotional, energetic, dark, uplifting, playful, quirky, etc.)
- Production style instead of specific instruments
- Rich atmospheric and mood descriptions

Provide a concise, natural description suitable for music library search.`;
};